import { makeAutoObservable } from 'mobx';
import { Service } from '../model/professional/service-model';

/**
 * Types pour les modales
 */
export type ModalMode = 'create' | 'edit' | 'view';

/**
 * Interface pour les états de modal
 */
export interface ModalState<T> {
  isOpen: boolean;
  mode: ModalMode;
  selected: T | null;
}

class UIStore {
  /**
   * États pour les filtres et recherche
   */
  searchTerm = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';

  /**
   * États pour les modales
   */
  serviceModal: ModalState<Service> = {
    isOpen: false,
    mode: 'create',
    selected: null
  };

  /**
   * Liste des callbacks pour fermer les modals
   * Permet de fermer tous les modals depuis n'importe où
   */
  private modalCloseCallbacks: Set<() => void> = new Set();

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Enregistre un callback de fermeture de modal
   * À appeler dans le useEffect de chaque modal
   */
  registerModalCloseCallback(callback: () => void) {
    this.modalCloseCallbacks.add(callback);
    return () => {
      this.modalCloseCallbacks.delete(callback);
    };
  }

  /**
   * Ferme tous les modals ouverts
   * Utilisé quand une erreur de limite est détectée
   */
  closeAllModals() {
    this.modalCloseCallbacks.forEach(callback => callback());
    this.closeServiceModal(); // Ferme aussi les modals gérés par le store
  }
  
  /**
   * Méthodes pour la recherche et les filtres
   */
  setSearchTerm(term: string) {
    this.searchTerm = term;
  }
  
  setStatusFilter(filter: 'all' | 'active' | 'inactive') {
    this.statusFilter = filter;
  }
  
  /**
   * Méthodes pour les modales de service
   */
  openServiceModal(mode: ModalMode, service?: Service | null) {
    this.serviceModal = {
      isOpen: true,
      mode,
      selected: service || null
    };
  }
  
  closeServiceModal() {
    this.serviceModal = {
      ...this.serviceModal,
      isOpen: false,
      selected: null
    };
  }
  
  /**
   * Getters pour les états des modales
   */
  get isServiceModalOpen() {
    return this.serviceModal.isOpen;
  }
  
  get selectedService() {
    return this.serviceModal.selected;
  }
  
  get serviceModalMode() {
    return this.serviceModal.mode;
  }
  
  /**
   * Méthodes pour définir les éléments sélectionnés
   */
  setSelectedService(service: Service | null) {
    this.serviceModal.selected = service;
  }
}

const uiStore = new UIStore();
export default uiStore;
